// ============================================
// SHARED PHYSICS MATH UTILITIES
// ============================================
// Single source of truth for Highland formula, kinematics, etc.
// Imported by physicsEngine.js, SimulationContext.jsx, useSimulation.js

import { C, MCS_CONSTANT_GEV } from './constants.js';

/**
 * Relativistic kinematics: E² = p² + m²
 * @param {number} momentum - GeV/c
 * @param {number} mass - GeV/c²
 * @returns {{ energy: number, beta: number, gamma: number }}
 */
export const calculateKinematics = (momentum, mass) => {
    const energy = Math.sqrt(momentum * momentum + mass * mass);
    const beta = momentum / energy;
    const gamma = energy / mass;
    return { energy, beta, gamma };
};

/**
 * Highland formula: θ_RMS = (13.6 MeV / βcp) �- |z| �- √(L/X₀) �- [1 + 0.038 ln(L/X₀)]
 * @param {number} momentum - GeV/c
 * @param {number} mass - GeV/c²
 * @param {number} thickness - cm
 * @param {number} X0 - radiation length in cm
 * @param {number} charge - particle charge in units of e (default: 1)
 * @returns {number} θ_RMS in radians
 */
export const calculateMCSAngle = (momentum, mass, thickness, X0, charge = 1) => {
    const { beta } = calculateKinematics(momentum, mass);
    const betaP = beta * momentum;
    const ratio = thickness / X0;
    if (ratio <= 0) return 0;
    const z = Math.abs(charge);
    const logCorrection = 1 + 0.038 * Math.log(ratio);
    const thetaRMS = (MCS_CONSTANT_GEV * z / betaP) * Math.sqrt(ratio) * logCorrection;
    return thetaRMS;
};

/**
 * MCS variance = θ_RMS²
 */
export const calculateMCSVariance = (momentum, mass, thickness, X0, charge = 1) => {
    const theta = calculateMCSAngle(momentum, mass, thickness, X0, charge);
    return theta * theta;
};

/**
 * Decay length in lab frame: λ = βcγτ₀
 */
export const calculateDecayLength = (beta, gamma, lifetime) => {
    return beta * C * gamma * lifetime;
};

/**
 * Lab lifetime: τ_lab = γ �- τ₀
 */
export const calculateLabLifetime = (gamma, properLifetime) => {
    return gamma * properLifetime;
};
